import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// One invite per second keeps a large list well inside the API's 100 requests
// a minute per address, with room left for the page's own requests. A 429 is
// waited out once and retried before it is counted as a failure.
const PACE_MS = 1000;
const RATE_LIMIT_WAIT_MS = 30000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// "Invite all to portal": every active customer with an email and no portal
// account (GET /api/portal/invite-candidates), invited one at a time through
// the same POST /api/portal/invite/:id the per-row button uses. The server
// answers 409 { skipped } for a customer who set up an account after the list
// was loaded, which is counted as skipped rather than re-invited.
//
// Do Not Serve customers come back from the candidates endpoint as a separate
// doNotServe list. They are never sent an invite and are reported as skipped.
function BulkPortalInviteModal({ show, onClose, onFinished }) {
  const [phase, setPhase] = useState('loading'); // loading | confirm | sending | done | error
  const [candidates, setCandidates] = useState([]);
  const [doNotServe, setDoNotServe] = useState([]);
  const [progress, setProgress] = useState(0);
  const [sent, setSent] = useState(0);
  const [skipped, setSkipped] = useState([]);
  const [failed, setFailed] = useState([]);
  const [error, setError] = useState('');
  const stopRef = useRef(false);

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    stopRef.current = false;
    setPhase('loading');
    setProgress(0);
    setSent(0);
    setSkipped([]);
    setFailed([]);
    setError('');
    axios.get(`${API_URL}/api/portal/invite-candidates`)
      .then((res) => {
        if (cancelled) return;
        setCandidates(res.data.customers || []);
        setDoNotServe(res.data.doNotServe || []);
        setPhase('confirm');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.error || 'Could not load customers to invite');
        setPhase('error');
      });
    return () => { cancelled = true; stopRef.current = true; };
  }, [show]);

  const inviteOne = async (c) => {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const res = await axios.post(`${API_URL}/api/portal/invite/${c.id}`, null, {
          params: { onlyIfNoAccount: '1' },
        });
        return res.data.inviteSent
          ? { outcome: 'sent' }
          : { outcome: 'failed', reason: 'Invite created but the email was not sent' };
      } catch (err) {
        const status = err.response?.status;
        if (status === 409 && err.response?.data?.skipped) {
          return { outcome: 'skipped', reason: err.response.data.error };
        }
        if (status === 429 && attempt === 0) {
          await wait(RATE_LIMIT_WAIT_MS);
          continue;
        }
        return { outcome: 'failed', reason: err.response?.data?.error || err.message || 'Request failed' };
      }
    }
    return { outcome: 'failed', reason: 'Rate limited' };
  };

  const start = async () => {
    setPhase('sending');
    setSkipped(doNotServe.map((c) => ({ name: c.name, reason: 'Do not serve' })));
    for (let i = 0; i < candidates.length; i += 1) {
      if (stopRef.current) break;
      const c = candidates[i];
      const r = await inviteOne(c);
      if (r.outcome === 'sent') setSent((n) => n + 1);
      else if (r.outcome === 'skipped') setSkipped((l) => l.concat({ name: c.name, reason: r.reason }));
      else setFailed((l) => l.concat({ name: c.name, reason: r.reason }));
      setProgress(i + 1);
      if (i < candidates.length - 1) await wait(PACE_MS);
    }
    setPhase('done');
    if (onFinished) onFinished();
  };

  const close = () => {
    stopRef.current = true;
    if (phase === 'sending' && onFinished) onFinished();
    onClose();
  };

  if (!show) return null;

  const pendingCount = candidates.filter((c) => c.pending).length;

  return (
    <div className="modal-overlay" onClick={phase === 'sending' ? undefined : close}>
      <div
        className="modal"
        style={{ maxWidth: '520px', maxHeight: '85vh', overflow: 'auto' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-invite-title"
      >
        <div className="modal-header">
          <h2 id="bulk-invite-title">Invite all to portal</h2>
          <button className="modal-close" onClick={close} aria-label="Close">&times;</button>
        </div>

        <div style={{ padding: '0 24px 8px' }}>
          {phase === 'loading' && <p>Finding customers to invite...</p>}
          {phase === 'error' && <div className="alert alert-error">{error}</div>}

          {phase === 'confirm' && candidates.length === 0 && (
            <p>Every active customer with an email already has a portal account. There is no one to invite.</p>
          )}
          {phase === 'confirm' && doNotServe.length > 0 && (
            <p className="text-muted" style={{ fontSize: '0.875rem' }}>
              {doNotServe.length} do not serve customer{doNotServe.length === 1 ? ' is' : 's are'} skipped and will not be invited.
            </p>
          )}
          {phase === 'confirm' && candidates.length > 0 && (
            <>
              <p>
                Send a portal invitation to <strong>{candidates.length}</strong> customer{candidates.length === 1 ? '' : 's'}:
                every active customer with an email and no portal account.
              </p>
              {pendingCount > 0 && (
                <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                  {pendingCount} of them already have an unaccepted invite. They get a new one, and the old link stops working.
                </p>
              )}
              <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                Invites go out one per second, so this takes about {Math.max(1, Math.ceil(candidates.length / 60))} minute{candidates.length > 60 ? 's' : ''}. Keep this window open until it finishes.
              </p>
            </>
          )}

          {(phase === 'sending' || phase === 'done') && (
            <>
              <p aria-live="polite">
                {phase === 'sending' ? `Sending ${progress} of ${candidates.length}...` : `Finished: ${progress} of ${candidates.length} processed.`}
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
                <li>Sent: <strong>{sent}</strong></li>
                <li>Skipped: <strong>{skipped.length}</strong></li>
                <li>Failed: <strong>{failed.length}</strong></li>
              </ul>
              {phase === 'done' && failed.length > 0 && (
                <div className="alert alert-error" style={{ fontSize: '0.875rem' }}>
                  {failed.map((f, i) => <div key={i}>{f.name}: {f.reason}</div>)}
                </div>
              )}
              {phase === 'done' && skipped.length > 0 && (
                <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                  {skipped.map((f, i) => <div key={i}>{f.name}: {f.reason}</div>)}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
          {phase === 'confirm' && candidates.length > 0 && (
            <>
              <button className="btn btn-outline" onClick={close}>Cancel</button>
              <button className="btn btn-primary" onClick={start}>
                Send {candidates.length} invite{candidates.length === 1 ? '' : 's'}
              </button>
            </>
          )}
          {phase === 'sending' && (
            <button className="btn btn-outline" onClick={() => { stopRef.current = true; }}>Stop</button>
          )}
          {(phase === 'done' || phase === 'error' || (phase === 'confirm' && candidates.length === 0)) && (
            <button className="btn btn-primary" onClick={close}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default BulkPortalInviteModal;
